const EXCEL_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const EXCEL_CELL_LIMIT = 32767;
const MIN_COL_WIDTH = 10;
const MAX_COL_WIDTH = 60;

// Identifier-like columns stay text so Excel keeps leading zeros and long digit runs.
// Match whole words only: "Voltage", "Percentage", "Paid" and "Idle Fee" must stay numeric.
const TEXT_WORDS = new Set(['id', 'ids', 'serial', 'rfid', 'tag', 'phone']);
const isTextHeader = (header) =>
  String(header)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .some((word) => TEXT_WORDS.has(word));

// Plain decimal number without a meaningful leading zero ("0123" stays text).
const isPlainNumber = (text) => /^-?(0|[1-9]\d{0,14})(\.\d+)?$/.test(text);

const sanitizeCell = (value, asText) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value) && !asText) return value;
  let text = String(value);
  if (!asText && isPlainNumber(text)) return Number(text);
  if (!/^-?\d+(\.\d+)?$/.test(text) && /^[=+\-@]/.test(text)) {
    text = `'${text}`;
  }
  if (text.length > EXCEL_CELL_LIMIT) {
    text = text.slice(0, EXCEL_CELL_LIMIT);
  }
  return text;
};

export const sanitizeExcelFileName = (name, fallback = 'export') => {
  const cleaned = String(name ?? '')
    .replace(/[\\/:*?"<>|]/g, '_')
    .trim();
  return cleaned || fallback;
};

export async function downloadExcel(rows, filename) {
  // exceljs is ~1 MB; load it only when someone actually exports.
  const { default: ExcelJS } = await import('exceljs/dist/exceljs.min.js');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Export');
  const list = Array.isArray(rows) ? rows : [];
  const headers = list.length ? Object.keys(list[0]) : [];

  if (headers.length) {
    const headerRow = sheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
    sheet.views = [{ state: 'frozen', ySplit: 1 }];

    const textColumns = headers.map((header) => isTextHeader(header));
    const widths = headers.map((header) => String(header).length);
    list.forEach((row) => {
      const cells = headers.map((header, index) =>
        sanitizeCell(row?.[header], textColumns[index]),
      );
      cells.forEach((cell, index) => {
        const len = cell == null ? 0 : String(cell).length;
        if (len > widths[index]) widths[index] = len;
      });
      sheet.addRow(cells);
    });
    widths.forEach((width, index) => {
      sheet.getColumn(index + 1).width = Math.min(
        MAX_COL_WIDTH,
        Math.max(MIN_COL_WIDTH, width + 2),
      );
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: EXCEL_MIME });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    String(filename).toLowerCase().endsWith('.xlsx') ? filename : `${filename}.xlsx`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
